<template>
  <div class="w-full h-full">
    <!-- Loading Overlay -->
    <vue-loading
      v-if="isLoading"
      background-color="rgba(255,255,255,1)"
      :active="isLoading"
      color="#4F41A3"
      :width="60"
      :height="60"
      :is-full-page="true"
    />

    <!-- Contenido principal -->
    <div
      v-show="!isLoading"
      class="w-full bg-zt-white h-full flex flex-col relative"
      :style="[bgUrl]"
    >
      <div
        class="h-full flex justify-center items-center flex-row z-10 lg:px-24 xl:px-56"
        :class="[loginBoxPosition, { 'lg:justify-end': !bgUrl }]"
      >
        <div v-if="!bgUrl" class="hidden w-full flex justify-start lg:block">
          <div class="w-3/5">
            <graphic-base
              class="w-full"
              height="626"
              width="599"
              :viewBox="'0 0 626 599'"
            >
              <default-background-image />
            </graphic-base>
          </div>
        </div>
        <div :class="!routeIsFalabella ? 'w-11/12 login-box-width' : ''">
          
            <router-view></router-view>
          
        </div>
      </div>
      <div
        v-if="!bgUrl"
        class="hidden horizon-bar absolute w-full z-0 lg:block"
      ></div>
      <transition name="slide-up" mode="out-in">
        <zt-auth-footer v-if="showFooter" :footer-styles="footerStyles" />
      </transition>
      <zt-auth-login-help-dialog :isActiveModalInfo="isActiveModalInfo" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ztAuthFooter from '@/modules/Auth/components/authFooter'
import convertHexToRGBA from '@/utils/convertHexToRGBA'
import { useWindowResize } from '@sichern/ui-elements'
import { store, mutation } from '@/modules/Auth/hooks/activeLoginModal'
import { storeUser, mutationUser } from '@/modules/Auth/hooks/passUserBlockData'
import {
  storeUserPass,
  mutationUserPass
} from '@/modules/Auth/hooks/passExpiredUserData'
import ZtAuthLoginHelpDialog from '@/modules/Auth/components/authLoginHelpDialog'
import GraphicBase from '@/components/commons/Illustrations/graphicBase'
import DefaultBackgroundImage from '@/components/commons/Illustrations/graphicSVG/defaultBackgroundImage'

export default {
  name: 'ztAuthLayout',
  components: {
    DefaultBackgroundImage,
    GraphicBase,
    ZtAuthLoginHelpDialog,
    ztAuthFooter
  },
  provide: {
    store,
    mutation,
    storeUser,
    mutationUser,
    storeUserPass,
    mutationUserPass
  },
  data() {
    return {
      objectLayoutParams: {},
      windowSize: useWindowResize(this),
      isLoading: false,
      isInIframe: false,
      iframeTimeout: null
    }
  },
  computed: {
    ...mapGetters({ getLoginCustomParams: 'auth/getLoginCustomParams' }),
    bgUrl() {
      const layout = this.objectLayoutParams.layout
      if (typeof layout.background.url !== 'undefined') {
        return {
          backgroundImage: `url(${layout.background.url})`,
          backgroundSize: 'cover',
          backgroundPosition: layout.background.position
        }
      }
      return ''
    },
    footerStyles() {
      const layout = this.objectLayoutParams.layout
      return {
        bgFooter: {
          backgroundColor: convertHexToRGBA(
            layout.footer.bgColor,
            layout.footer.transparent
          )
        },
        textColor: {
          color: layout.footer.textColor
        }
      }
    },
    loginBoxPosition() {
      const layout = this.objectLayoutParams.layout
      let position = ''
      switch (layout.loginBox.position) {
        case 'left':
          position = 'start'
          break
        case 'right':
          position = 'end'
          break
        default:
          position = 'center'
          break
      }
      return layout.loginBox.position ? `lg:justify-${position}` : ''
    },
    isMobile() {
      return this.windowSize.width < 1024
    },
    showFooter() {
      return (
        this.$route.name === 'login' ||
        (this.$route.name === 'firstLogin' && !this.isMobile) ||
        (this.$route.name === 'forgotPassword' && !this.isMobile) ||
        (this.$route.name === 'resetPassword' && !this.isMobile) ||
        (this.$route.name === 'userBlock' && !this.isMobile) ||
        (this.$route.name === 'passwordExpired' && !this.isMobile) ||
        (this.$route.name === 'mfaEmailVerification' && !this.isMobile) ||
        (this.$route.name === 'mfaCodeVerification' && !this.isMobile) ||
        (this.$route.name === 'mfaUserBlock' && !this.isMobile) ||
        (this.$route.name === 'linkUserEmail' && !this.isMobile) ||
        (this.$route.name === 'linkUserPassword' && !this.isMobile)
      )
    },
    isActiveModalInfo() {
      return store.activeModal
    },
    routeIsFalabella() {
      return this.$route.name === 'checkingAccountKeyCloak'
    }
  },
  created() {
    this.objectLayoutParams = this.getLoginCustomParams.loginCustom
    
    // Detectar si estamos en un iframe
    this.isInIframe = window.self !== window.top
    
    if (this.isInIframe) {
      // Mostrar loading mientras esperamos el token
      this.isLoading = true
      
      // Timeout de 2 minutos (120000ms)
      this.iframeTimeout = setTimeout(() => {
        this.isLoading = false
      }, 120000)
    }
  },
  mounted() {
    // Escuchar evento para ocultar el loading desde componentes hijos
    this.$root.$on('hide-layout-loading', () => {
      this.isLoading = false
      if (this.iframeTimeout) {
        clearTimeout(this.iframeTimeout)
      }
    })
  },
  beforeUnmount() {
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout)
    }
  }
}
</script>

<style scoped lang="scss">
.horizon-bar {
  height: 280px;
  background-color: #d2d9f8;
  bottom: 77px;
}
.slide-right-enter-active,
.slide-right-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-right-leave-active {
  opacity: 0;
  transform: translate(3em, 0);
}

.slide-right-enter {
  opacity: 0;
  transform: translate(-3em, 0);
}

.slide-up-enter-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
  transform: translateY(0px);
}
.slide-up-leave-active,
.slide-up-enter-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(74px);
}
.login-box-width {
  width: 100%;
  max-width: 448px;
}
</style>
